import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  Shield,
  Eye,
  BarChart3,
  FileCheck,
  AlertCircle,
  TrendingUp,
  Network,
  Globe,
  FileText,
  ArrowRight,
  EyeOff,
  CreditCard,
  FileSearch,
  Building2,
  Printer,
  Package,
  Gift,
  Shirt,
  PanelTop,
  Tag,
  Settings,
  ClipboardCheck,
  MessageSquare,
  Award,
  Truck,
  ChartLine,
} from "lucide-react";

export default function ForProcurement() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="For Procurement Teams - Enterprise Control"
        description="Enterprise procurement solution with AI-powered specifications, centralized vendor management, spend visibility, and comprehensive audit trails."
        canonical="/for-procurement"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            <span className="text-primary">Procurement Control</span> for Brand Materials
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Give your teams access to an organized vendor marketplace while maintaining centralized control—no more maverick 
            spending or vendor sprawl. One platform, complete visibility, documented compliance workflows.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth/signup?type=procurement">
              Request Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          </div>
          
          {/* Vendor Network Centralization Diagram */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="relative py-12">
              {/* Labels */}
              <div className="absolute top-0 left-4 md:left-8 text-sm font-medium text-muted-foreground">
                Vendor Network
              </div>
              <div className="absolute top-0 right-4 md:right-8 text-sm font-medium text-muted-foreground">
                Your Organization
              </div>
              
              <div className="flex items-center justify-between gap-4 md:gap-8">
                {/* Vendor Nodes - Left Side */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  {[
                    { icon: Printer, label: "Printers" },
                    { icon: Package, label: "Packaging" },
                    { icon: Gift, label: "Promo" },
                    { icon: Shirt, label: "Apparel" },
                    { icon: PanelTop, label: "Signage" },
                    { icon: Tag, label: "Labels" },
                  ].map((vendor, index) => (
                    <div key={index} className="flex flex-col items-center gap-2">
                      <div className="relative">
                        <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-muted border-2 border-dashed border-primary/30 flex items-center justify-center">
                          <vendor.icon className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" />
                        </div>
                        {/* Connection line indicator */}
                        <div className="absolute top-1/2 -right-2 w-2 h-0.5 bg-primary/30" />
                      </div>
                      <span className="text-xs text-muted-foreground">{vendor.label}</span>
                    </div>
                  ))}
                </div>
                
                {/* Connection Lines */}
                <div className="hidden md:flex flex-col items-center gap-1 px-4">
                  <div className="w-16 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                  <div className="w-20 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                  <div className="w-24 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                  <div className="w-24 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                  <div className="w-20 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                  <div className="w-16 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary" />
                </div>
                
                {/* Radius Hub - Center */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <div className="relative">
                    <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-primary shadow-lg shadow-primary/25 flex items-center justify-center ring-4 ring-primary/20">
                      <span className="text-primary-foreground font-bold text-sm md:text-base">Radius</span>
                    </div>
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-center text-muted-foreground whitespace-nowrap">
                      Single Control Point
                    </div>
                  </div>
                </div>
                
                {/* Connection to Client */}
                <div className="hidden md:block w-16 h-1 bg-gradient-to-r from-primary to-primary/60 rounded-full" />
                
                {/* Client Node - Right Side */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                    <Building2 className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground">You</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <EyeOff className="h-7 w-7 text-destructive" />
              </div>
              <h3 className="font-semibold">Lack of Visibility</h3>
              <p className="text-sm text-muted-foreground">
                Brand teams buying directly from dozens of vendors without procurement oversight or tracking.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <Network className="h-7 w-7 text-destructive" />
              </div>
              <h3 className="font-semibold">Vendor Sprawl</h3>
              <p className="text-sm text-muted-foreground">
                Uncontrolled vendor proliferation without proper vetting, contracts, or performance tracking.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <CreditCard className="h-7 w-7 text-destructive" />
              </div>
              <h3 className="font-semibold">Maverick Spending</h3>
              <p className="text-sm text-muted-foreground">
                Decentralized purchasing leading to budget overruns and non-compliant vendor relationships.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <FileSearch className="h-7 w-7 text-destructive" />
              </div>
              <h3 className="font-semibold">Audit Complexity</h3>
              <p className="text-sm text-muted-foreground">
                Difficult to track spend, contracts, and compliance across fragmented vendor relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Procurement Control Tools</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Everything you need to maintain governance while enabling your teams
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            icon={Network}
            title="Managed Verified Marketplace"
            description="Your teams access a verified vendor network through one centralized platform. No more unauthorized vendor relationships or maverick spending."
          />
          <FeatureCard
            icon={Eye}
            title="Centralized Spend Visibility"
            description="Real-time dashboard of all brand materials purchasing across the verified vendor network with complete transparency."
          />
          <FeatureCard
            icon={Shield}
            title="Custom Vendor Criteria"
            description="Build and enforce approved vendor criteria, teams can only engage vendors that meet your organization's requirements."
          />
          <FeatureCard
            icon={FileCheck}
            title="Compliance Tracking"
            description="Track vendor certification status, insurance certificates, platform performance, and real user feedback."
          />
          <FeatureCard
            icon={FileText}
            title="Complete Audit Trail"
            description="Every transaction across all vendors documented in one system with supporting documentation."
          />
          <FeatureCard
            icon={BarChart3}
            title="Spend Analytics"
            description="Comprehensive reports on spending by category, vendor, department, and project across entire network."
          />
          <FeatureCard
            icon={AlertCircle}
            title="Risk Mitigation"
            description="Payment protection, uniform quality standards and contract language ensure consistency among partners."
          />
          <FeatureCard
            icon={Globe}
            title="AI-Guided Standards"
            description="AI ensures specification completeness and consistency. Promote positive outcomes without the need for costly expert input. Drive consistency and reduce complexity."
          />
          <FeatureCard
            icon={TrendingUp}
            title="Vendor Performance"
            description="Platform-wide performance data shows ratings, reviews, and delivery metrics from all client transactions—not just your organization. Make decisions based on real project outcomes across the network."
          />
        </div>
      </section>

      {/* Network Control Callout */}
      <section className="border-y bg-primary/5 py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">Verified Vendor Network Without Vendor Sprawl</h2>
            <p className="text-xl text-muted-foreground">
              All network vendors complete enrollment verification including business license, insurance documentation, and capability certification review.
            </p>
            <Link to="/verification" className="text-primary hover:underline inline-flex items-center gap-1">
              How verification works <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">1</div>
                <div className="text-sm text-muted-foreground">Master Contract</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">100%</div>
                <div className="text-sm text-muted-foreground">Audit Trail</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">1</div>
                <div className="text-sm text-muted-foreground">Compliance Standard</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">Verified</div>
                <div className="text-sm text-muted-foreground">Vendor Marketplace</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Procurement Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Cost Savings</h3>
            <p className="text-sm text-muted-foreground">
              Competitive bidding requirements typically reduce costs by 15-30% compared to direct vendor relationships.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Eliminate Vendor Sprawl</h3>
            <p className="text-sm text-muted-foreground">
              Replace dozens of direct vendor relationships with one managed platform and unified processes.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Network Scale, Single Control Point</h3>
            <p className="text-sm text-muted-foreground">
              Give teams access to verified specialized vendors without losing procurement oversight or governance.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Compliance Assurance</h3>
            <p className="text-sm text-muted-foreground">
              Every purchase follows policy with complete documentation, audit trails, and regulatory compliance.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Risk Reduction</h3>
            <p className="text-sm text-muted-foreground">
              Vendor enrollment requirements, purchase protection, and delivery verification protect the organization from risk.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Empowered Teams</h3>
            <p className="text-sm text-muted-foreground">
              Brand teams can still move quickly and access verified vendor network within procurement guardrails.
            </p>
          </div>
        </div>
      </section>

      {/* Procurement Journey */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Procurement Journey</h2>
          <div className="max-w-3xl mx-auto relative">
            {/* Vertical connecting line */}
            <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary hidden md:block" />
            
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  icon: Settings,
                  title: "Configure Policies & Workflows",
                  description: "Set up procurement policies, approval chains, spend thresholds, and organizational requirements."
                },
                {
                  step: "2",
                  icon: Network,
                  title: "Curate Approved Verified Vendor Network",
                  description: "Build preferred vendor lists from platform-verified vendors. Set your organization's certification and compliance requirements."
                },
                {
                  step: "3",
                  icon: MessageSquare,
                  title: "Brand Team Creates RFQ with AI",
                  description: "Brand teams describe projects in plain English—AI guides specification creation within policy guidelines."
                },
                {
                  step: "4",
                  icon: ClipboardCheck,
                  title: "Procurement Review & Approval",
                  description: "Procurement reviews and approves requests based on configured thresholds and policies."
                },
                {
                  step: "5",
                  icon: FileText,
                  title: "Competitive Bids Received",
                  description: "Multiple bids from verified approved vendors are received and evaluated—all centralized in one system."
                },
                {
                  step: "6",
                  icon: Award,
                  title: "Award to Selected Vendor",
                  description: "Select your winning vendor based on bid value and compliance with your requirements. All contract terms tracked centrally."
                },
                {
                  step: "7",
                  icon: Truck,
                  title: "Monitor Delivery Centrally",
                  description: "Track milestone completion, delivery verification, and payment releases through the platform."
                },
                {
                  step: "8",
                  icon: ChartLine,
                  title: "Analyze Spend & Performance",
                  description: "Review spend analytics and vendor performance data across entire verified network to inform future decisions."
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-6">
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div className="pt-1 space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        Step {item.step}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Perfect For</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Enterprise Standardization</h3>
            <p className="text-sm text-muted-foreground">
              Fortune 500 marketing procurement standardization across divisions and business units with centralized control.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Multi-Brand Organizations</h3>
            <p className="text-sm text-muted-foreground">
              Vendor consolidation into managed network while allowing brand autonomy within governance framework.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Financial Services Compliance</h3>
            <p className="text-sm text-muted-foreground">
              Meet strict compliance and audit requirements for vendor spend and regulatory oversight.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Healthcare Regulatory</h3>
            <p className="text-sm text-muted-foreground">
              Support regulatory compliance for patient materials production with complete documentation and vendor enrollment requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="border-y bg-muted/50 py-16">
        <div className="container text-center space-y-4">
          <h2 className="text-3xl font-bold">Your Vendors. Your Policies. Our Platform.</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Maintain complete governance while providing network access. Enable teams without sacrificing control or compliance.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="container py-20">
        <div className="text-center space-y-6">
          <h2 className="text-3xl font-bold">Bring Control to Brand Procurement</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Verified vendor network. Complete control. Better outcomes. Join procurement teams already transforming brand materials management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=procurement">
                Request Demo
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/features">
                Explore Features
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
